import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MoveRight, Star } from "lucide-react";
import { Button } from "./button";
import { Link } from "react-router-dom";
import { useThemeMode } from "../../contexts/ThemeContext";

function Hero() {
  const [titleNumber, setTitleNumber] = useState(0);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  
  const titles = useMemo(
    () => ["healthspan", "lifespan", "wellness"],
    []
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (titleNumber === titles.length - 1) {
        setTitleNumber(0);
      } else {
        setTitleNumber(titleNumber + 1);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [titleNumber, titles]);

  return (
    <div className="w-full relative overflow-hidden" style={{
      backgroundColor: isDark ? '#0f0f0f' : '#ffffff'
    }}>
      <div className="container mx-auto relative z-10">
        <div className="flex gap-4 py-8 lg:py-16 items-center justify-center flex-col">
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{
              backgroundColor: isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
              border: isDark ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid rgba(16, 185, 129, 0.15)'
            }}>
              <Star className="w-4 h-4" style={{ color: '#10b981' }} />
              <span className="text-sm font-medium" style={{
                color: isDark ? '#ffffff' : '#1f2937'
              }}>Trusted by 5,000+ Users</span>
            </div>
          </div>
          <div className="flex gap-2 flex-col">
            <h1 className="text-5xl md:text-7xl max-w-4xl tracking-tighter text-center font-bold">
              <span style={{
                color: isDark ? '#ffffff' : '#111827'
              }}>Increase Your</span>
              <span className="relative flex w-full justify-center text-center py-2 md:py-3 min-h-[90px] md:min-h-[110px] items-center" style={{ overflow: 'visible' }}>
                {titles.map((title, index) => (
                  <motion.span
                    key={index}
                    className="absolute font-semibold leading-tight"
                    style={{
                      background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      lineHeight: '1.2'
                    }}
                    initial={{ opacity: 0, y: "-100" }}
                    transition={{ type: "spring", stiffness: 50 }}
                    animate={
                      titleNumber === index
                        ? {
                            y: 0,
                            opacity: 1,
                          }
                        : {
                            y: titleNumber > index ? -150 : 150,
                            opacity: 0,
                          }
                    }
                  >
                    {title}
                  </motion.span>
                ))}
              </span>
            </h1>

            <p className="text-lg md:text-xl leading-relaxed tracking-tight max-w-3xl text-center" style={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
              Join thousands who are actively expanding their health outcomes with our scientifically efficient health decision tree. 
              Build lasting health habits, track your progress, and unlock your full potential with science-backed protocols.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/register">
              <Button 
                size="lg" 
                className="gap-3 px-8 py-6 text-lg font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300"
                style={{
                  background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                  border: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(90deg, #059669 0%, #047857 100%)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(90deg, #10b981 0%, #059669 100%)';
                }}
              >
                Start Your Journey
              </Button>
            </Link>
            <Link to="/login">
              <Button 
                size="lg" 
                variant="outline" 
                className="gap-3 px-8 py-6 text-lg font-semibold border-2 transition-all duration-300"
                style={{ 
                  borderColor: isDark ? 'rgba(55, 65, 81, 1)' : 'rgba(209, 213, 219, 1)',
                  color: isDark ? '#ffffff' : '#374151',
                  backgroundColor: isDark ? 'rgba(17, 24, 39, 0.5)' : 'rgba(249, 250, 251, 0.8)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#10b981';
                  e.currentTarget.style.color = '#10b981';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = isDark ? 'rgba(55, 65, 81, 1)' : 'rgba(209, 213, 219, 1)';
                  e.currentTarget.style.color = isDark ? '#ffffff' : '#374151';
                }}
              >
                Sign In <MoveRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export { Hero };
